import { apiFetchJson } from "./apiClient";
import {
  CampaignAsset,
  AddAssetToCampaignRequest,
  CampaignAssetAccessRequest,
  AssetTableItem,
  CampaignAssetSection,
  CharacterSummary,
  CampaignAssetDiceTemplate,
} from "../types/types";

export interface CreateCampaignAssetSectionRequest {
  name: string;
  content: string;
  dmNotes?: string; // Private DM-only notes, never shared
  sectionOrder?: number;
}

export interface UpdateCampaignAssetSectionRequest {
  name: string;
  content: string;
  dmNotes?: string; // Private DM-only notes, never shared
  sectionOrder?: number;
}

export interface CreateCampaignAssetFromScratchRequest {
  name: string;
  type: string;
  category: string;
  description?: string;
  dmNotes?: string; // Private DM-only notes, never shared
  parentId?: number;
  characterAccess?: number[];
  tags?: string;
}

export class CampaignAssetService {
  // Fetch section-level access for an asset
  static async getSectionAccess(
    campaignId: number,
    assetId: number
  ): Promise<{ sectionId: number; characterIds: number[] }[]> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets/${assetId}/sections/access`);
  }

  // Get all assets in a campaign
  static async getSharedCampaignAssets(campaignId: number): Promise<CampaignAsset[]> {
    const data = await apiFetchJson<CampaignAsset[]>(
      `/api/protected/campaigns/${campaignId}/assets/shared`
    );
    return data ?? [];
  }

  // Get a single campaign asset with processed references
  static async getCampaignAsset(campaignId: number, assetId: number): Promise<CampaignAsset> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets/${assetId}`);
  }

  // Add an asset to a campaign
  static async addAssetToCampaign(
    campaignId: number,
    request: AddAssetToCampaignRequest
  ): Promise<CampaignAsset> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets`, {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  // Grant character access to a campaign asset
  static async grantAssetAccess(
    campaignId: number,
    assetId: number,
    request: CampaignAssetAccessRequest
  ): Promise<{ message: string }> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets/${assetId}/access`, {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  // Replace (set) the full access list for a campaign asset (asset + sections)
  static async setAssetAccess(
    campaignId: number,
    assetId: number,
    request: CampaignAssetAccessRequest
  ): Promise<{ message: string }> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets/${assetId}/access`, {
      method: "PUT",
      body: JSON.stringify(request),
    });
  }

  // Revoke character access from a campaign asset
  static async revokeAssetAccess(
    campaignId: number,
    assetId: number,
    request: CampaignAssetAccessRequest
  ): Promise<{ message: string }> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets/${assetId}/access`, {
      method: "DELETE",
      body: JSON.stringify(request),
    });
  }

  // Get user's assets (for adding to campaign) with hierarchy
  static async getUserAssets(): Promise<AssetTableItem[]> {
    return apiFetchJson("/api/protected/assets");
  }

  // Get asset hierarchy for drag and drop
  static async getAssetsHierarchy(): Promise<AssetTableItem[]> {
    const data = await apiFetchJson<AssetTableItem[]>("/api/protected/assets/hierarchy");
    return data ?? [];
  }

  // Update campaign asset
  static async updateCampaignAsset(
    campaignId: number,
    assetId: number,
    data: { name?: string; description?: string; dmNotes?: string }
  ): Promise<CampaignAsset> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets/${assetId}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  // Move a campaign asset to a new parent (or promote to root when newParentId is null)
  static async reparentAsset(
    campaignId: number,
    assetId: number,
    newParentId: number | null
  ): Promise<{ message: string }> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets/${assetId}/parent`, {
      method: "PATCH",
      body: JSON.stringify({ parentId: newParentId ?? 0 }),
    });
  }

  // Remove asset from campaign
  static async removeAssetFromCampaign(
    campaignId: number,
    assetId: number
  ): Promise<{ message: string }> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets/${assetId}`, {
      method: "DELETE",
    });
  }

  // Create campaign asset section
  static async createCampaignAssetSection(
    campaignId: number,
    assetId: number,
    request: CreateCampaignAssetSectionRequest
  ): Promise<CampaignAssetSection> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets/${assetId}/sections`, {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  // Update campaign asset section
  static async updateCampaignAssetSection(
    campaignId: number,
    assetId: number,
    sectionId: number,
    request: UpdateCampaignAssetSectionRequest
  ): Promise<CampaignAssetSection> {
    return apiFetchJson(
      `/api/protected/campaigns/${campaignId}/assets/${assetId}/sections/${sectionId}`,
      {
        method: "PUT",
        body: JSON.stringify(request),
      }
    );
  }

  // Delete campaign asset section
  static async deleteCampaignAssetSection(
    campaignId: number,
    assetId: number,
    sectionId: number
  ): Promise<{ message: string }> {
    return apiFetchJson(
      `/api/protected/campaigns/${campaignId}/assets/${assetId}/sections/${sectionId}`,
      { method: "DELETE" }
    );
  }

  // Validate if an asset can be added to a campaign (checks references)
  static async validateAssetForCampaign(
    campaignId: number,
    assetId: number
  ): Promise<{
    canAdd: boolean;
    missingAssets: AssetTableItem[];
    warnings: string[];
  }> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets/${assetId}/validate`);
  }

  // Get campaign assets in hierarchical format
  static async getCampaignAssetsHierarchy(campaignId: number): Promise<CampaignAsset[]> {
    const data = await apiFetchJson<CampaignAsset[]>(
      `/api/protected/campaigns/${campaignId}/assets/hierarchy`
    );
    return data ?? [];
  }

  // Create a campaign-only (scratch) asset with no personal library link
  static async createCampaignAssetFromScratch(
    campaignId: number,
    request: CreateCampaignAssetFromScratchRequest
  ): Promise<CampaignAsset> {
    return apiFetchJson(`/api/protected/campaigns/${campaignId}/assets/scratch`, {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  // ── Knowledge view endpoints ─────────────────────────────────────────────────

  /**
   * Returns the authenticated user's characters enrolled in the given campaign.
   * Used to populate the character selector in the Knowledge view.
   */
  static async getMyCampaignCharacters(campaignId: number): Promise<CharacterSummary[]> {
    const data = await apiFetchJson<CharacterSummary[]>(
      `/api/protected/campaigns/${campaignId}/characters/mine`
    );
    return data ?? [];
  }

  /**
   * Returns the campaign asset tree filtered to only the assets accessible to
   * the given character (or the full tree if the requester is the DM).
   */
  static async getCharacterKnowledgeTree(
    campaignId: number,
    characterId: number
  ): Promise<CampaignAsset[]> {
    const data = await apiFetchJson<CampaignAsset[]>(
      `/api/protected/campaigns/${campaignId}/knowledge/${characterId}`
    );
    return data ?? [];
  }

  // ── Fate Formulas on Chronicle Entries (read-only, GM only) ──────────────

  static async getCampaignAssetDiceTemplates(
    campaignId: number,
    campaignAssetId: number
  ): Promise<CampaignAssetDiceTemplate[]> {
    const data = await apiFetchJson<CampaignAssetDiceTemplate[]>(
      `/api/protected/campaigns/${campaignId}/assets/${campaignAssetId}/dice-templates`
    );
    return data ?? [];
  }

  static async createCampaignAssetDiceTemplate(
    campaignId: number,
    campaignAssetId: number,
    req: {
      name: string;
      diceNotation: string;
      modifier: number;
      description: string;
      skillType: string;
    }
  ): Promise<CampaignAssetDiceTemplate> {
    return apiFetchJson(
      `/api/protected/campaigns/${campaignId}/assets/${campaignAssetId}/dice-templates`,
      {
        method: "POST",
        body: JSON.stringify(req),
      }
    );
  }

  static async updateCampaignAssetDiceTemplate(
    campaignId: number,
    campaignAssetId: number,
    templateId: number,
    req: {
      name: string;
      diceNotation: string;
      modifier: number;
      description: string;
      skillType: string;
    }
  ): Promise<CampaignAssetDiceTemplate> {
    return apiFetchJson(
      `/api/protected/campaigns/${campaignId}/assets/${campaignAssetId}/dice-templates/${templateId}`,
      {
        method: "PUT",
        body: JSON.stringify(req),
      }
    );
  }

  static async deleteCampaignAssetDiceTemplate(
    campaignId: number,
    campaignAssetId: number,
    templateId: number
  ): Promise<void> {
    return apiFetchJson(
      `/api/protected/campaigns/${campaignId}/assets/${campaignAssetId}/dice-templates/${templateId}`,
      {
        method: "DELETE",
      }
    );
  }
}
